import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  RouterStateSnapshot,
} from '@angular/router';
import { GaAuthService } from '@ga/central-auth-angular';
import { RuntimeConfigurationService } from 'gain-web/shared-modules/runtime-configuration/runtime-configuration.service';
import { markPortalLoginRedirectInFlight, stashReturnUrl } from './return-url';

/**
 * Gates the root authenticated route so a RESOLVED auth state is established
 * before the route's data resolvers run.
 *
 * The resolvers each fetch a BFF API, which lazily calls
 * `GaAuthService.getAccessToken()`. If that token round-trip fails (refresh
 * token genuinely expired / logged out) a resolver would hard-throw, aborting
 * navigation and leaving a blank router outlet (bug 7719533). By driving the
 * same token resolution here first, an unauthenticated user is sent cleanly to
 * the portal login (preserving their deep URL) instead of hitting a blank page.
 *
 * For the non-portal auth flows (basic/dev tokens) `getAccessToken` resolves a
 * fake token and this guard simply allows navigation, so their behavior is
 * unchanged.
 */
@Injectable({
  providedIn: 'root',
})
export class PortalAuthGuard implements CanActivate {
  constructor(
    private authService: GaAuthService,
    private runtimeConfigService: RuntimeConfigurationService,
  ) {}

  async canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean> {
    const authOptions = this.runtimeConfigService.get().authOptions;
    try {
      await this.authService.getAccessToken({
        requestedRoles: authOptions.requestRoles ?? [],
      });
      return true;
    } catch {
      // Genuine, resolved "not authenticated": preserve the deep URL and send
      // the user to the portal login rather than letting a resolver blank the
      // page.
      stashReturnUrl(state.url);
      this.redirectToLogin(authOptions.loginPageUrl);
      return false;
    }
  }

  /** Full-page navigation to the portal login. Isolated so it can be overridden in tests. */
  protected redirectToLogin(loginPageUrl: string): void {
    markPortalLoginRedirectInFlight();
    window.location.href = loginPageUrl;
  }
}
